import { Public_Routes } from '@common/url';
import { Dialog } from '@prophecy/ui-v3/Dialog';
import { toast } from '@prophecy/ui-v3/Toast';
import { BASE_PATH, navigateAndRefresh } from '@prophecy/utils/history';
import { ShareDetails } from 'frontend/core/src/data/apis/types';
import { matchPath } from 'react-router-dom';

import { getShareDetails } from '../../../../data/apis/api';
import { shareToken } from '../../../../utils/shareToken';

let tokenRefreshTimer: NodeJS.Timeout | null = null;
const REFRESH_TOKEN_INTERVAL = 50 * 60 * 1000;

export async function getShareDetailsUrl(shareId: string) {
  let response = undefined;
  try {
    response = await getShareDetails(shareId);
  } catch (error) {
    const allowedDialogResult = await Dialog.warning({
      title: 'Unable to Access Shared Project',
      'data-test-id': 'unable-acccess-share-project',
      subTitle:
        "This shared project is either unavailable or you don't have permission to view it. Please check the share link or contact the sharer.",
      children: null,
      closeButton: null,
      size: 'l',
      closable: false,
      okButton: { children: 'Continue', block: false }
    });
    if (allowedDialogResult === Dialog.ResponseAction.okButton) {
      navigateAndRefresh(Public_Routes.SignIn.getUrl(undefined));
    }
    return;
  }

  if (response) {
    if (response.jwtToken) {
      shareToken.set(response.jwtToken);
      scheduleNextRefresh(shareId);
    }
    shareToken.shareDetails = response.shareDetails;
    shareToken.hasDirectAccess = Boolean(response.hasDirectAccess);
    shareToken.loggedInUserId = response?.loggedInUser;
  }

  const url = buildShareUrl(response);

  return { url };
}

export const getShareId = (pathname: string, searchParams: URLSearchParams) => {
  if (pathname.includes('/share/')) {
    const shareId = pathname.split('/').at(-1);
    return shareId ?? null;
  }
  const shareIdFromParams = searchParams.get('shareId');
  return shareIdFromParams ?? null;
};

export const isSharedProject = (url: string) => {
  const targetPathname = new URL(BASE_PATH + url, window.location.origin).pathname;
  return Boolean(matchPath({ path: targetPathname, end: true }, window.location.pathname));
};

const buildShareUrl = (data: ShareDetails | null) => {
  if (!data?.shareDetails) {
    return null;
  }
  const url = new URL(window.location.href);
  const isOnboardingTour = url.searchParams.get('onboardingTour') === 'true';

  const { projectUID, entityURI, shareId } = data?.shareDetails ?? {};
  const baseUrl = `/sql/${projectUID}`;
  const shareParam = !data?.hasDirectAccess && shareId ? `&shareId=${shareId}` : '';
  const onboardingTourParam = isOnboardingTour ? `&onboardingTour=true` : '';
  return `${baseUrl}?${entityURI}${shareParam}${onboardingTourParam}`;
};

const refreshShareToken = async (shareId: string) => {
  if (!shareId) return;

  try {
    const response = await getShareDetails(shareId);
    if (response?.jwtToken) {
      shareToken.set(response.jwtToken);
      scheduleNextRefresh(shareId);
    }
    shareToken.hasDirectAccess = Boolean(response?.hasDirectAccess);
    shareToken.shareDetails = response?.shareDetails;
    shareToken.loggedInUserId = response?.loggedInUser;
  } catch (error) {
    console.error('Error refreshing share token:', error);
    throw error;
  }
};

function scheduleNextRefresh(shareId: string) {
  if (!shareId) return;
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
  }
  // Set timeout to refresh token after 50 minutes (10 minutes before 60-minute expiry)
  tokenRefreshTimer = setTimeout(async () => {
    try {
      await refreshShareToken(shareId);
    } catch (error) {
      toast.error({
        content: 'Failed to refresh share token',
        'data-test-id': 'failred-to-refresh-token'
      });
    }
  }, REFRESH_TOKEN_INTERVAL);
}
